import {
  Box,
  Chip,
  Grid,
  Stack,
  Typography,
  TextField,
} from '@atgfr8/atg-ui-tailwind';
import { Button, ToastType, RadioGroup } from '@components';
import { createForm } from '@felte/solid';
import { validator } from '@felte/validator-yup';
import { closeModal } from '@store/modals';
import {
  ICreditCardPayment,
  customerStore,
  makeCreditCardPayments,
  orderStore,
} from '@store/orders';
import { userStore } from '@store/user';
import { IOrderViewModel } from '@typeDefinitions/orderTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { formatAmount, handleToast } from '@utils/utils';
import { PAY_WITH_CREDIT_CARD_ID } from '@views/order/constants';
import { For, createSignal, onMount } from 'solid-js';
import * as yup from 'yup';

import classes from './classes';
import { payWithCreditCardFormSchema } from './validations';

export const PayWithCreditCardModal = () => {
  const [loading, setLoading] = createSignal<boolean>(false);
  const modalClass = classes.payWithCreditCardModal;

  const computeTotalPayments = () => {
    return orderStore.order.payments.length > 0
      ? orderStore.order.payments
          .filter((v) => v.operationType !== 'Delete')
          .reduce((acc, item) => acc + item.amount, 0)
      : 0;
  };

  const computeTotalLineItems = () => {
    return orderStore.order.lineItems.length > 0
      ? orderStore.order.lineItems
          .filter((v) => v.operationType !== 'Delete')
          .reduce(
            (acc, item) => acc + Number(item.rate) * Number(item.quantity),
            0,
          )
      : 0;
  };

  const totalAmount = () => computeTotalLineItems() - computeTotalPayments();

  const creditCardFee = () => (3 / 100) * totalAmount();

  const [totalPaymentWithFee, setTotalPaymentWithFee] = createSignal<number>(
    totalAmount() + creditCardFee(),
  );

  const getCurrentSelectedCustomerContactId = () => {
    return [
      ...customerStore.customer.billingContacts,
      ...customerStore.customer.freightContacts,
    ].find((i) => i.id === orderStore.order.customerContactId);
  };

  const [newToEmail, setNewToEmail] = createSignal('');
  const [emailError, setEmailError] = createSignal<string | null>(null);

  const addEmail = (email: string) => {
    const trimmed = email.trim();
    if (!trimmed || !yup.string().email().isValidSync(trimmed)) {
      setEmailError('Invalid email address');
      return;
    }
    const current: string[] = Array.isArray(data().to)
      ? (data().to as string[])
      : [];
    if (current.includes(trimmed)) {
      setEmailError('Email already exists');
      return;
    }
    setFields('to', [...current, trimmed]);
    setNewToEmail('');
    setEmailError(null);
  };

  const removeEmail = (index: number) => {
    const current: string[] = Array.isArray(data().to)
      ? (data().to as string[])
      : [];
    const updated = current.filter((_, i) => i !== index);
    setFields('to', updated);
    if (updated.length === 0) setEmailError('Email is required');
  };

  const handleEmailKeyPress = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addEmail(newToEmail());
    }
  };

  const getBodyContent = (orderDetails: IOrderViewModel) => {
    return `Armstrong Invoice #${orderDetails.id} \nReference Number #${
      orderDetails.referenceNumber
    }\nAmount Due: ${formatAmount(
      totalPaymentWithFee(),
    )} \n\nThank you for your business!`;
  };

  const { form, data, setFields, errors } = createForm<
    yup.InferType<typeof payWithCreditCardFormSchema>
  >({
    initialValues: {
      to: [getCurrentSelectedCustomerContactId()?.email, userStore.user.email]
        .filter((email): email is string => !!email)
        .map((email) => email.replace(/"/g, '').trim())
        .filter((email, i, arr) => email !== '' && arr.indexOf(email) === i),
      subject: `Armstrong Invoice #${orderStore.order.id}`,
      body: getBodyContent(orderStore.order),
      fee: true,
    },
    extend: validator({ schema: payWithCreditCardFormSchema }),
    onSubmit: async (values) => {
      setLoading(true);
      await handleSubmit(values)
        .then(() => {
          closeModal(PAY_WITH_CREDIT_CARD_ID);
        })
        .finally(() => setLoading(false));
    },
  });

  const handleFeeChange = (value: boolean) => {
    setTotalPaymentWithFee(
      value === true ? totalAmount() + creditCardFee() : totalAmount(),
    );
    setFields('body', getBodyContent(orderStore.order));
  };

  const handleSubmit = async (
    values: yup.InferType<typeof payWithCreditCardFormSchema>,
  ) => {
    const payload: ICreditCardPayment = {
      emailSettings: {
        to: values.to as string[],
        subject: values.subject,
        body: values.body,
      },
      fee: values.fee,
    };

    try {
      await makeCreditCardPayments(orderStore.order.id, payload);
      closeModal(PAY_WITH_CREDIT_CARD_ID);
    } catch (error) {
      handleToast(
        ToastType.Error,
        'Failed to send Credit Card Payments to Customer',
      );
    }
  };

  onMount(() => {
    totalAmount();
    creditCardFee();
  });

  return (
    <form ref={form}>
      <Box style={{ 'flex-grow': 1 }}>
        <Grid container spacing={2}>
          <Grid item size={12}>
            <Stack spacing={2}>
              <Stack direction="row" class="flex-wrap" gap={1}>
                <For each={data().to as string[]}>
                  {(email, index) => (
                    <Chip
                      id={`chip-to-${index()}`}
                      text={email}
                      isDeleteIcon
                      onDelete={() => removeEmail(index())}
                    />
                  )}
                </For>
              </Stack>
              <TextField
                label="Email To"
                dataTestId={'to-multiEmailInput'}
                type="email"
                value={newToEmail()}
                onChange={setNewToEmail}
                onKeyPress={handleEmailKeyPress}
                error={emailError()}
              />
            </Stack>
          </Grid>
          <Grid item size={12}>
            <TextField
              label="Subject"
              dataTestId={'subject-textInput'}
              value={data().subject}
              name="subject"
              onChange={(val: string) => setFields('subject', val)}
              error={errors().subject}
            />
          </Grid>
          <Grid item size={12}>
            <Typography
              variant="body1"
              component="p"
              class={modalClass.subheading}
            >
              Credit Card Fee
            </Typography>
          </Grid>
          <Grid item size={{ xs: 12, md: 8 }}>
            <RadioGroup
              direction="vertical"
              optionClass="w-full! bg-[#E8F0F6]! rounded-[5px]! items-baseline! py-[10px] pr-[60px] pl-[17px]"
              options={[
                {
                  label: (
                    <Box class="flex flex-col">
                      <span class={modalClass.radioLabelHeading}>
                        Add Credit Card Fee
                      </span>
                      <span class={modalClass.radioLabelHelperText}>
                        This will pass on a {formatAmount(creditCardFee())} fee
                        to the customer
                      </span>
                    </Box>
                  ),
                  value: true,
                },
                {
                  label: (
                    <Box class="flex flex-col">
                      <span class={modalClass.radioLabelHeading}>
                        No Credit Card Fee
                      </span>
                      <span class={modalClass.radioLabelHelperText}>
                        This will reduce your profit by{' '}
                        {formatAmount(creditCardFee())}
                      </span>
                    </Box>
                  ),
                  value: false,
                },
              ]}
              value={data().fee}
              onChange={(value) => {
                setFields('fee', value);
                handleFeeChange(value);
              }}
              error={errors().fee}
            />
          </Grid>
          <Grid item size={{ xs: 12, md: 4 }}>
            <Stack class={modalClass.priceSectionContainer} spacing={1}>
              <Box textAlign="center" class="flex flex-col">
                <span>Invoice Amount:</span>
                <span class={modalClass.amount}>
                  {formatAmount(totalAmount())}
                </span>
              </Box>
              <Box textAlign="center" class="flex flex-col">
                <span>Fee:</span>
                <span class={modalClass.amount}>
                  {formatAmount(data().fee === true ? creditCardFee() : 0)}
                </span>
              </Box>
              <Box textAlign="center" class="flex flex-col">
                <span>Amount Due:</span>
                <span class={modalClass.amount}>
                  {formatAmount(totalPaymentWithFee())}
                </span>
              </Box>
            </Stack>
          </Grid>
          <Grid item size={12}>
            <TextField
              multiline
              rows={7}
              label="Body"
              value={data().body}
              error={errors().body}
              dataTestId={'body-textArea'}
              onChange={(val) => setFields('body', val)}
            />
          </Grid>
        </Grid>
        <Grid item size={12} class="flex justify-end items-center gap-2 pt-3">
          <Button
            label="Done"
            type="submit"
            variant="contained"
            isLoading={loading()}
            disabled={customerStore.loading}
            dataTestId={formatDataTestId('Done', 'button')}
          />
          <Button
            label="Cancel"
            variant="text"
            onClick={() => closeModal(PAY_WITH_CREDIT_CARD_ID)}
            dataTestId={formatDataTestId('Cancel', 'button')}
          />
        </Grid>
      </Box>
    </form>
  );
};
